// src/modules/app-views/products/pages/Document/modals/StockMovementModal/Step1.jsx
// *******************************************************************************
// STOCK MOVEMENT MODAL - STEP 1
// *******************************************************************************
// Line 1: Movement type (entry/exit) + Warehouse select
// Line 2: Quantity + Description
// Edit mode: type always disabled, qty/warehouse disabled if not last movement.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Row, Col } from 'antd';
import SimpleSegment from 'components/SimpleComponents/SimpleSegment';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import translateX from 'utils/translateX';
import appService from 'services/appService';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const Step1Content = ({ isEdit = false, isLastMovement = false }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const movementOptions = useMemo(() => [
    { value: 'entry', label: translateX('entry') },
    { value: 'exit', label: translateX('stock_exit') },
  ], []);

  const warehouseOptions = useMemo(() => {
    const raw = appService.getModulePreferences({
      moduleId: 'products',
      category: 'warehouses',
    });
    if (!Array.isArray(raw)) return [];
    return raw.filter(w => !w.removed).map(w => ({
      value: w.uid,
      label: `${w.code} - ${w.name}`,
    }));
  }, []);

  // Edit rules: type/qty/warehouse editable only if last movement
  const isTypeLocked = isEdit && !isLastMovement;
  const isQtyLocked = isEdit && !isLastMovement;
  const isWarehouseLocked = isEdit && !isLastMovement;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const field = isQtyLocked ? 'description' : 'quantity';
    const timer = setTimeout(() => {
      const el = document.getElementById(field);
      if (el) el.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, [isQtyLocked]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Line 1: Movement Type + Warehouse */}
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <SimpleSegment
            name="movementType"
            label="movement_type"
            options={movementOptions}
            block
            disabled={isTypeLocked}
          />
        </Col>
        <Col xs={24} sm={12}>
          <SimpleSelect
            name="stockUid"
            label="warehouse"
            options={warehouseOptions}
            required
            disabled={isWarehouseLocked}
          />
        </Col>
      </Row>

      {/* Line 2: Quantity + Description */}
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={6}>
          <SimpleNumber
            name="quantity"
            label="quantity"
            min={1}
            required
            disabled={isQtyLocked}
          />
        </Col>
        <Col xs={24} sm={18}>
          <SimpleInput
            name="description"
            label="description"
            required
            placeholder="movement_description_placeholder"
          />
        </Col>
      </Row>
    </>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const step1 = ({ isEdit = false, isLastMovement = false } = {}) => ({
  stepTitle: 'stock_movement',
  validationFields: ['quantity', 'description', 'stockUid'],
  content: <Step1Content isEdit={isEdit} isLastMovement={isLastMovement} />,
  onEnter: ({ goFocus }) => goFocus?.(isEdit && !isLastMovement ? 'description' : 'quantity'),
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step1;
